// Composer — the message box. Grows with its text; Enter sends (Shift+Enter = new line;
// on touch screens Enter is a new line); Esc stops a running turn. Files come in through
// the paperclip, drag-and-drop anywhere on the page, or paste; each uploads straight into
// the project's uploads/ folder with a progress bar, then travels with the message.
// Focusing or typing wakes a sleeping workspace early (onPrewake), hiding the cold start.

import { h, Component } from 'preact';
import { Icon, fileIcon } from './icons.js';
import { transcribe } from '../api.js';
import { fmtSize } from './transcript.js';

const MAX_FILE = 50 * 1024 * 1024;
const coarse = () => typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
let nextId = 1;

function uploadFile(pid, file, name, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/projects/${pid}/upload?name=${encodeURIComponent(name)}`);
    xhr.withCredentials = true;
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded / e.total); };
    xhr.onload = () => {
      let body = null;
      try { body = JSON.parse(xhr.responseText); } catch (e) { /* not json */ }
      if (xhr.status >= 200 && xhr.status < 300 && body) resolve(body);
      else reject(new Error((body && body.detail) || `upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error('network error during upload'));
    xhr.send(file);
  });
}

const MAX_CLIP_MS = 5 * 60 * 1000;      // five minutes; longer belongs in a file, not a prompt

export class Composer extends Component {
  // --- dictation -----------------------------------------------------------
  // Recorded in the browser, transcribed on the account's own AI credits, and dropped into
  // the box as text the person can still edit before sending. Deliberately not "speak and
  // send": a transcript is a draft, and the one thing worse than mistyping is a machine
  // sending something you did not get to read.
  async toggleMic() {
    if (this.state.recording) { this.stopMic(); return; }
    if (!navigator.mediaDevices || typeof MediaRecorder === 'undefined') {
      this.setState({ micError: 'This browser cannot record audio.' });
      return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      // Refusing the microphone is a choice, not a fault: say what happened, once.
      this.setState({ micError: 'No microphone access.' });
      return;
    }
    const chunks = [];
    const rec = new MediaRecorder(stream);
    rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
    rec.onstop = async () => {
      stream.getTracks().forEach((t) => t.stop());
      clearTimeout(this._micT);
      const blob = new Blob(chunks, { type: rec.mimeType || 'audio/webm' });
      this.setState({ recording: false, transcribing: true, micError: '' });
      try {
        const b64 = await new Promise((res, rej) => {
          const fr = new FileReader();
          fr.onload = () => res(String(fr.result).split(',')[1] || '');
          fr.onerror = rej;
          fr.readAsDataURL(blob);
        });
        const r = await transcribe(b64, blob.type, 'clip.webm');
        const text = (r && r.text || '').trim();
        if (text) {
          const cur = this.state.text || '';
          this.setState({ text: cur ? cur.replace(/\s*$/, '') + ' ' + text : text },
            () => { this.grow(); this.ta && this.ta.focus(); });
        } else {
          this.setState({ micError: 'Nothing was said, or it could not be made out.' });
        }
      } catch (e) {
        this.setState({ micError: e.message || 'Could not transcribe that.' });
      }
      this.setState({ transcribing: false });
    };
    rec.start();
    this._rec = rec;
    // A recording nobody stops is a bill nobody meant to run up.
    this._micT = setTimeout(() => this.stopMic(), MAX_CLIP_MS);
    this.setState({ recording: true, micError: '' });
  }

  stopMic() {
    try { if (this._rec && this._rec.state !== 'inactive') this._rec.stop(); } catch (e) { /* */ }
  }

  constructor(p) {
    super(p);
    this.state = { text: '', files: [], dragging: false, recording: false, transcribing: false, micError: '' };
    this.ta = null; this.picker = null; this.dragDepth = 0;
    this.onDragEnter = (e) => { if (!this.canAttach() || !hasFiles(e)) return; this.dragDepth++; this.setState({ dragging: true }); };
    this.onDragOver = (e) => { if (this.canAttach() && hasFiles(e)) e.preventDefault(); };
    this.onDragLeave = () => { if (--this.dragDepth <= 0) { this.dragDepth = 0; this.setState({ dragging: false }); } };
    this.onDrop = (e) => {
      if (!this.canAttach() || !hasFiles(e)) return;
      e.preventDefault(); this.dragDepth = 0; this.setState({ dragging: false });
      this.addFiles([...e.dataTransfer.files]);
    };
  }

  canAttach() { return !this.props.disabled && (!!this.props.pid || !!this.props.ensureProject); }

  componentDidMount() {
    window.addEventListener('dragenter', this.onDragEnter);
    window.addEventListener('dragover', this.onDragOver);
    window.addEventListener('dragleave', this.onDragLeave);
    window.addEventListener('drop', this.onDrop);
    if (this.props.autoFocus && this.ta && !coarse()) this.ta.focus();
    this.grow();
  }

  componentWillUnmount() {
    window.removeEventListener('dragenter', this.onDragEnter);
    window.removeEventListener('dragover', this.onDragOver);
    window.removeEventListener('dragleave', this.onDragLeave);
    window.removeEventListener('drop', this.onDrop);
  }

  componentDidUpdate(prev) {
    // switching chats drops pending attachments; a new chat getting its first project id keeps them
    if (prev.pid && prev.pid !== this.props.pid) this.setState({ files: [] });
    if (prev.seed !== this.props.seed && this.props.seed) {
      this.setState({ text: this.props.seed.text || '' }, () => { this.grow(); this.ta && this.ta.focus(); });
    }
  }

  grow() {
    const ta = this.ta; if (!ta) return;
    if (CSS && CSS.supports && CSS.supports('field-sizing', 'content')) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, Math.round(window.innerHeight * 0.4)) + 'px';
  }

  async addFiles(list) {
    let pid = this.props.pid;
    if (!pid && this.props.ensureProject) {
      try { pid = await this.props.ensureProject(); } catch (e) { return; }
    }
    if (!pid) return;
    for (const file of list) {
      const id = nextId++;
      const name = file.name && file.name !== 'image.png' ? file.name : `pasted-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.${(file.type.split('/')[1] || 'bin').replace('jpeg', 'jpg')}`;
      const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
      const entry = { id, name, size: file.size, type: file.type, progress: 0, preview, path: null, error: null };
      if (file.size > MAX_FILE) entry.error = 'Larger than 50 MB';
      this.setState((s) => ({ files: [...s.files, entry] }));
      if (entry.error) continue;
      const patch = (p) => this.setState((s) => ({ files: s.files.map((f) => (f.id === id ? { ...f, ...p } : f)) }));
      if (this.props.onPrewake) this.props.onPrewake();
      uploadFile(pid, file, name, (progress) => patch({ progress }))
        .then((res) => patch({ path: res.path, name: res.name, type: res.type || file.type, progress: 1 }))
        .catch((err) => patch({ error: err.message }));
    }
  }

  remove(id) {
    this.setState((s) => {
      const f = s.files.find((x) => x.id === id);
      if (f && f.preview) URL.revokeObjectURL(f.preview);
      return { files: s.files.filter((x) => x.id !== id) };
    });
  }

  uploading() { return this.state.files.some((f) => !f.path && !f.error); }

  send() {
    const { text, files } = this.state;
    const ready = files.filter((f) => f.path);
    if (this.props.busy || this.uploading() || (!text.trim() && !ready.length)) return;
    this.props.onSend && this.props.onSend(text.trim(), ready.map((f) => ({ path: f.path, name: f.name, size: f.size, type: f.type })));
    files.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
    this.setState({ text: '', files: [] }, () => this.grow());
  }

  onKey(e) {
    if (e.key === 'Escape' && this.props.busy) { e.preventDefault(); this.props.onStop && this.props.onStop(); return; }
    if (e.key === 'Enter' && !e.shiftKey && !e.isComposing && !coarse()) { e.preventDefault(); this.send(); }
  }

  onPaste(e) {
    const files = [...(e.clipboardData && e.clipboardData.files || [])];
    if (files.length && this.canAttach()) { e.preventDefault(); this.addFiles(files); }
  }

  render(p, { text, files, dragging }) {
    const ready = files.some((f) => f.path);
    const canSend = !p.busy && !this.uploading() && (text.trim() || ready) && !p.disabled;
    return h('div', { className: 'composer' + (p.busy ? ' busy' : '') },
      dragging ? h('div', { className: 'drop-overlay' }, h('div', null, h(Icon, { name: 'clip', size: 28 }), h('p', null, 'Drop files to add them to this chat'))) : null,
      h('div', { className: 'composer-box' },
        files.length ? h('div', { className: 'chips-row' }, files.map((f) =>
          h('div', { key: f.id, className: 'fchip' + (f.error ? ' bad' : ''), title: f.error || f.name },
            f.preview ? h('img', { src: f.preview, alt: '' }) : h(Icon, { name: fileIcon(f.name, f.type), size: 18 }),
            h('span', { className: 'fchip-text' }, h('b', null, f.name), h('small', null, f.error || (f.path ? fmtSize(f.size) : `${Math.round(f.progress * 100)}%`))),
            !f.path && !f.error ? h('span', { className: 'fchip-bar', style: { width: `${Math.round(f.progress * 100)}%` } }) : null,
            h('button', { type: 'button', className: 'fchip-x', 'aria-label': `Remove ${f.name}`, onClick: () => this.remove(f.id) }, h(Icon, { name: 'x', size: 12 }))))) : null,
        h('textarea', {
          ref: (el) => { this.ta = el; }, rows: 1, value: text, disabled: p.disabled,
          placeholder: p.placeholder || 'Ask for anything — a summary, a chart, a document, a page…', 'aria-label': 'Message',
          onInput: (e) => { this.setState({ text: e.target.value }); this.grow(); if (p.onPrewake) p.onPrewake(); },
          onFocus: () => p.onPrewake && p.onPrewake(),
          onKeyDown: (e) => this.onKey(e), onPaste: (e) => this.onPaste(e),
        }),
        h('div', { className: 'composer-bar' },
          h('button', { type: 'button', className: 'icon-btn', 'aria-label': 'Attach files', title: 'Attach files',
            disabled: !this.canAttach(), onClick: () => this.picker && this.picker.click() }, h(Icon, { name: 'clip', size: 18 })),
          h('button', { type: 'button',
            className: 'icon-btn' + (this.state.recording ? ' recording' : ''),
            'aria-label': this.state.recording ? 'Stop recording' : 'Dictate',
            'aria-pressed': this.state.recording ? 'true' : 'false',
            title: this.state.transcribing ? 'Transcribing…' : (this.state.recording ? 'Stop recording' : 'Dictate'),
            disabled: !!this.state.transcribing,
            onClick: () => this.toggleMic() },
            h(Icon, { name: this.state.recording ? 'stop' : 'shell', size: 18 })),
          this.state.micError ? h('span', { className: 'mic-err' }, this.state.micError) : null,
          h('input', { type: 'file', multiple: true, hidden: true, ref: (el) => { this.picker = el; },
            onChange: (e) => { this.addFiles([...e.target.files]); e.target.value = ''; } }),
          h('span', { className: 'composer-hint' }, p.busy ? 'Esc to stop' : (p.hint || '')),
          p.busy
            ? h('button', { type: 'button', className: 'send stop', 'aria-label': 'Stop', title: 'Stop (Esc)', onClick: () => p.onStop && p.onStop() }, h(Icon, { name: 'stop', size: 16 }))
            : h('button', { type: 'button', className: 'send', 'aria-label': 'Send', title: 'Send (Enter)', disabled: !canSend, onClick: () => this.send() }, h(Icon, { name: 'send', size: 18 })))));
  }
}

function hasFiles(e) {
  const t = e.dataTransfer && e.dataTransfer.types;
  return !!t && [...t].includes('Files');
}
